import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { BROKER_REQUEST_STATUSES, BROKER_REQUEST_LABELS, type BrokerRequestStatus } from '@wasity/shared';
import { Card, EmptyState, ErrorNotice, Input, PageHeader, Pagination, Select, Skeleton, Table, Toolbar } from '@/components/ui';
import { useI18n, formatRelative } from '@/i18n';
import { api, qs } from '@/lib/api';
import type { BR, UserRow } from '@/lib/types';
import { AdminBrChip, CommissionChip } from './common';

const LIMIT = 50;

/**
 * The search box, held back a moment before it becomes a request.
 *
 * The search is the server's now — filtering the page already in the browser told an admin that a
 * record on the next page did not exist — so every keystroke would otherwise be a round trip.
 */
function useDebounced(value: string, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export function AdminRequests() {
  const { t, locale, pick, localTitle } = useI18n();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | BrokerRequestStatus>('');
  const [brokerId, setBrokerId] = useState('');
  const [offset, setOffset] = useState(0);
  const search = useDebounced(q);
  // A new search starts at the first page. Without this, typing while on page 3 searched from row 100.
  useEffect(() => setOffset(0), [search]);
  const list = useQuery({ queryKey: ['admin', 'br', 'list', status, brokerId, search, offset], queryFn: () => api.get<{ items: BR[] }>(`/api/admin/broker-requests${qs({ status, brokerUserId: brokerId, q: search, limit: LIMIT, offset })}`) });
  const brokers = useQuery({ queryKey: ['admin', 'users', 'broker', 'all'], queryFn: () => api.get<{ items: UserRow[] }>('/api/admin/users?role=broker&limit=200'), staleTime: 60_000 });
  const rows = list.data?.items ?? [];
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/admin', label: t('nav.dashboard') }, { label: t('nav.brokerRequests') }]} title={t('nav.brokerRequests')} />
      <Toolbar end={list.data ? t('admin.requests.totalN', { n: rows.length }) : ''}>
        <Input className="w-[260px] max-w-full" placeholder={t('admin.requests.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('app.search')} />
        <Select className="w-[200px]" value={status} onChange={(e) => { setStatus(e.target.value as '' | BrokerRequestStatus); setOffset(0); }} aria-label={t('app.status')}>
          <option value="">{t('admin.requests.allStatuses')}</option>
          {BROKER_REQUEST_STATUSES.map((s) => (
            <option key={s} value={s}>{pick(BROKER_REQUEST_LABELS[s])}</option>
          ))}
        </Select>
        <Select className="w-[200px]" value={brokerId} onChange={(e) => { setBrokerId(e.target.value); setOffset(0); }} aria-label={t('app.broker')}>
          <option value="">{t('admin.requests.allBrokers')}</option>
          {brokers.data?.items.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </Select>
      </Toolbar>
      {list.isLoading ? <Skeleton lines={8} /> : list.error ? <ErrorNotice error={list.error} retry={() => void list.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} /> : rows.length ? (
        <Card className="p-0 overflow-hidden">
          <Table className="border-0 rounded-none">
            <thead><tr><th>{t('app.number')}</th><th>{t('app.property')}</th><th>{t('app.owner')}</th><th>{t('app.broker')}</th><th>{t('app.status')}</th><th>{t('app.commission')}</th><th>{t('app.updated')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="num"><Link to={`/admin/broker-requests/${r.id}`}>{r.number}</Link></td>
                  <td>{localTitle(r)}</td>
                  <td>{r.ownerName ?? '—'}</td>
                  <td className={r.brokerName ? '' : 'text-muted'}>{r.brokerName ?? '—'}</td>
                  <td><AdminBrChip br={r} /></td>
                  <td><CommissionChip br={r} /></td>
                  <td className="text-muted text-[13px] whitespace-nowrap">{formatRelative(r.updatedAt, locale)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : (
        <EmptyState title={t('app.noResults')} />
      )}
      <Pagination offset={offset} limit={LIMIT} count={list.data?.items.length ?? 0} onChange={setOffset} labels={{ prev: t('app.prev'), next: t('app.next'), page: t('app.page') }} />
    </div>
  );
}
